/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import Form from "@JohnLewisPartnership/checkout-forms/dist/components/form-simple/Form";
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import EMAIL from "@JohnLewisPartnership/checkout-forms/dist/form/fields/email";
import classnames from "classnames";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { InfoOutlined as InfoRegularIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InfoOutlined";
import { TickCircleOutlined as TickCircledRegular } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import analyticsTrigger from "../../utils/analytics/trigger/analyticsTrigger";
import styles from "./index.module.scss";
import ViewContactDetail from "./view-contact-details";
import getCallStatusConfig from "./call-status-config";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import Link from "next/link";
import FeatureFlagProvider from "@/context/FeatureFlagContext";
import createAxiosClient from "../../clients/axiosClient";
import { isFeatureEnabled } from "@/utils/feature-flags";

const EMAIL_ADDRESS = {
  ...EMAIL,
  maxLength: 60,
  value: null,
};

const REPAIR_REFERENCE = {
  component: Input,
  id: "callNumber",
  name: "callNumber",
  label: "Repair Reference",
  placeholder: "Enter your repair reference",
  className: "fieldLarge",
  minLength: 7,
  required: true,
  labelHelperElement: (
    <Tooltip icon>
      <span>
        Your repair reference is at least 7 digits long. You can find this on your confirmation email or paperwork.
      </span>
    </Tooltip>
  ),
  validationMessageKeys: {
    required: "Please enter your reference number",
  },
};

function Hero({ data }) {
  return (
    <div className={styles.heroContent}>
      <p>
        <b>Repair reference:</b> {data?.callNumber}
      </p>
      <p>
        <b>Item:</b> {data?.brand} {data?.model} {data?.productDescription}
      </p>
    </div>
  );
}

const getTrackingLink = (carrier, trackingNumber) => {
  if (!trackingNumber) {
    return null;
  }
  switch (carrier) {
    case "DPD":
      return `https://track.dpd.co.uk/parcels/${trackingNumber}`;
    case "A-Z":
      return `https://www.aftership.com/track/apc-overnight/${trackingNumber}`;
    case "Panther":
      return `https://pgtrack.com/${trackingNumber}`;
    case "UPS":
      return `https://wwwapps.ups.com/WebTracking/${trackingNumber}`;
    case "DHL":
      return `https://www.dhl.com/global-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${trackingNumber}`;
    default:
      return null;
  }
};

const offsetMap = ({ collectionCarrier, collectionTrackingNumber, returnCarrier, returnTrackingNumber }) => {
  return {
    "Field Service (Carrier)": {
      "repair booked": 11,
      "repair in progress": 20,
      "repair in progress - awaiting parts": 20,
      "repair in progress - awaiting information from the customer": 40,
      "on its way back": getTrackingLink(collectionCarrier, collectionTrackingNumber) ? 80 : 40,
    },
    "Carry In Service": {
      "repair booked": 11,
      "repair in progress": 20,
      "repair in progress - awaiting parts": 20,
      "repair in progress - awaiting information from the customer": 20,
      "on its way back": 30,
      "ready for collection": 110,
    },
    "Field Service": {
      "repair booked": 11,
      "repair in progress": 20,
      "repair in progress - awaiting parts": 20,
      "repair in progress - awaiting information from the customer": 40,
      "on its way back": getTrackingLink(returnCarrier, returnTrackingNumber) ? 80 : 40,
    },
  };
};

const eventMapFunc = ({
  callStatus,
  callTypeDescription,
  callStatusDescription,
  collectionAddress1,
  collectionAddress2,
  collectionTown,
  collectionPostalCode,
  storeName,
  collectionCarrier,
  collectionTrackingNumber,
  returnCarrier,
  returnTrackingNumber,
}) => ({
  "repair booked": () => {
    switch (callTypeDescription) {
      case "Field Service (Carrier)": {
        const trackingLink = getTrackingLink(collectionCarrier, collectionTrackingNumber);
        return {
          title: "Collection booked",
          description: (
            <div className={styles.description}>
              <p>{collectionCarrier || "The carrier"} will pick up your return from:</p>
              <div className={styles.address}>
                <p>{collectionAddress1}</p>
                <p>{collectionAddress2}</p>
                <p>{collectionTown}</p>
                <p>{collectionPostalCode}</p>
              </div>
              {trackingLink && (
                <PrimaryButton className={styles.progressButton} tag="a" href={trackingLink}>
                  Track your collection
                </PrimaryButton>
              )}
              <p>If you need to pack up your item in a specific way, we'll send you instructions by email.</p>
            </div>
          ),
        };
      }
      case "Carry In Service":
        return {
          title: "Repair booked",
          description: (
            <div className={styles.description}>
              <p>Your item is on its way to our repair centre.</p>
            </div>
          ),
        };
      case "Field Service":
        return {
          title: "Repair booked",
          description: (
            <div className={styles.description}>
              <p>
                We've booked you an appointment with an engineer. For more details, see your repair confirmation email.
              </p>
            </div>
          ),
        };
      default:
        return {
          title: "Repair booked",
        };
    }
  },
  "repair in progress": () => {
    switch (callStatusDescription.toLowerCase()) {
      case "repair in progress - awaiting information from the customer":
        if (callStatus.toLowerCase() === "on hold - fmip" || callStatus.toLowerCase() === "ohf") {
          return {
            title: "Repair in progress",
            description: (
              <div className={classnames(styles.description, styles.infoBox)}>
                <div>
                  <InfoRegularIcon />
                </div>
                <p>
                  We need to access your device to make your repair. Please contact us and have your repair reference
                  number ready.
                </p>
              </div>
            ),
          };
        }
        if (callStatus.toLowerCase() === "on hold - pop" || callStatus.toLowerCase() === "ohp") {
          return {
            title: "Repair in progress",
            description: (
              <div className={classnames(styles.description, styles.infoBox)}>
                <div>
                  <InfoRegularIcon />
                </div>
                <p>We need proof of purchase. Please contact us and have your repair reference number ready.</p>
              </div>
            ),
          };
        }
        return {
          title: "Repair in progress",
          description: (
            <div className={classnames(styles.description, styles.infoBox)}>
              <div>
                <InfoRegularIcon />
              </div>
              <p>
                We need to talk to you about your repair. please contact us and have your repair reference number ready.
              </p>
            </div>
          ),
        };
      case "repair in progress - awaiting parts":
        return {
          title: "Repair in progress",
          description: (
            <div className={classnames(styles.description, styles.infoBox)}>
              <div>
                <InfoRegularIcon />
              </div>
              <p>We're waiting for new parts for your item. Bear with us.</p>
            </div>
          ),
        };
      default:
        return {
          title: "Repair in progress",
          description: (
            <div className={styles.description}>
              <p>
                Good news! Your item is at our repair centre. We'll be in touch if we need any more details from you.
              </p>
            </div>
          ),
        };
    }
  },
  "on its way back": () => {
    if (callTypeDescription === "Field Service (Carrier)" || callTypeDescription === "Field Service") {
      const trackingLink = getTrackingLink(returnCarrier, returnTrackingNumber);
      return {
        title: "On its way to you",
        description: (
          <div className={styles.description}>
            <p>You'll have your item back soon</p>
            {trackingLink && (
              <PrimaryButton className={styles.progressButton} tag="a" href={trackingLink}>
                Track your package
              </PrimaryButton>
            )}
          </div>
        ),
      };
    }
    return {
      title: "On its way to you",
      description: (
        <div className={styles.description}>
          <p>Your item has left our repair centre.</p>
        </div>
      ),
    };
  },
  ...(callTypeDescription === "Carry In Service"
    ? {
        "ready for collection": () => ({
          title: "Waiting for collection",
          description: (
            <div className={styles.description}>
              <p>
                Your item can now be <b>collected from the following John Lewis store, {storeName}</b>. Please go to the
                technical support department.
              </p>
              <p>
                <b>You'll need to bring ID.</b>
              </p>
            </div>
          ),
        }),
      }
    : {}),
  "repair completed": () => ({
    title: "Complete",
    description: null,
  }),
});

const pageMap = {
  unknown: data => (
    <div className={styles.notFoundBox}>
      <div className={styles.icon}>
        <InfoRegularIcon />
      </div>
      <div className={styles.content}>
        <h3>Tracking not available</h3>
        <div className={styles.text}>
          <p>Don't worry, your repair is in hand. We're just having some technical issues.</p>
          <p>Feel free to contact us for an update on your repair.</p>
        </div>
        <hr className={styles.separator} />
        <ViewContactDetail callStatus={data?.callStatusDescription} />
      </div>
    </div>
  ),
  "repair completed": data => (
    <div className={styles.notFoundBox}>
      <div className={styles.heroBox}>
        <div className={classnames(styles.heroContainer, styles.textCenterDesktop)}>
          <Hero data={data} />
        </div>
      </div>
      <div className={styles.icon}>
        <TickCircledRegular />
      </div>
      <div className={styles.content}>
        <h3>Complete</h3>
        <div className={styles.text}>
          <p>If you need anything else, contact us and we'll be happy to help.</p>
        </div>
        <hr className={styles.separator} />
        <ViewContactDetail callStatus={data?.callStatusDescription} />
      </div>
    </div>
  ),
  "please contact us": data => (
    <div className={styles.notFoundBox}>
      <div className={styles.heroBox}>
        <div className={classnames(styles.heroContainer, styles.textCenterDesktop)}>
          <Hero data={data} />
        </div>
      </div>
      <div className={styles.icon} />
      <div className={styles.content}>
        <h3>Cancelled</h3>
        <div className={styles.text}>
          <p>Your item is no longer booked for repair.</p>
          <p>If you need anything else, contact us and we'll be happy to help.</p>
        </div>
        <hr className={styles.separator} />
        <ViewContactDetail callStatus={data?.callStatusDescription} />
      </div>
    </div>
  ),
};

const foundCallTypeDescriptions = ["Field Service (Carrier)", "Carry In Service", "Field Service"];

function RepairTracker({ data = null, error = null }) {
  const callStatusConfig = getCallStatusConfig(data?.callStatusDescription?.toLowerCase() || "unknown");
  const currentStatus =
    !foundCallTypeDescriptions.includes(data?.callTypeDescription) || error
      ? "unknown"
      : callStatusConfig.currentStatus;
  if (pageMap[currentStatus]) {
    return (
      <div className={styles.trackContainer}>
        <h1>Track your electrical repair</h1>
        {pageMap[currentStatus](data)}
      </div>
    );
  }
  const eventMap = eventMapFunc(data);
  const currentIndex = Object.keys(eventMap).findIndex(key => key === currentStatus);
  const events = Object.keys(eventMap).map((key, index) => ({
    status: key,
    renderFn: eventMap[key],
    hasHappened: index <= currentIndex,
  }));
  const progressPercent = currentIndex / (events.length - 1);
  const offset = offsetMap(data)[data?.callTypeDescription][data?.callStatusDescription?.toLowerCase()];
  const barStyle = {
    value: progressPercent,
    height: `calc(${progressPercent * 100}% - (${offset}px))`,
  };

  return (
    <div className={styles.trackContainer}>
      <h1>Track your electrical repair</h1>
      <div className={styles.progressBox}>
        <div className={styles.heroBox}>
          <div className={styles.heroContainer}>
            <Hero data={data} />
          </div>
        </div>
        <div className={styles.progressContainer}>
          <div className={styles.progressContent}>
            <h3>Progress</h3>
            <ul className={styles.eventsList}>
              <div>
                <div className={styles.bar} style={barStyle.value !== 83 ? {} : { height: barStyle.height }} />
                <div className={styles.progress} style={barStyle} />
              </div>
              {events.map((event, i) => {
                const { title, description } = event.renderFn();
                const pastStyle = i < currentIndex ? styles.statusPast : null;
                const futureStyle = i > currentIndex ? styles.statusFuture : null;
                return (
                  <li key={event.status} className={classnames(styles.statusCurrent, pastStyle, futureStyle)}>
                    <TickCircledRegular className={styles.statusIcon} />
                    <span className={styles.statusTitle}>{title}</span>
                    {event.status === currentStatus ? description : null}
                  </li>
                );
              })}
            </ul>
          </div>
          <hr className={styles.separator} />
          <div className={styles.contactDetails}>
            <ViewContactDetail callStatus={data?.callStatusDescription} />
          </div>
        </div>
      </div>
    </div>
  );
}

export const metadata = {
  title: `Track Your Electrical Repair | Customer Services`,
  description: `Have a question? We're here to help with all your queries, from delivery and returns to orders and your online account and your My John Lewis membership.`,
};

const getAnalyticsPageName = (data = {}) => {
  const { callTypeDescription, callStatusDescription } = data || {};
  return foundCallTypeDescriptions.includes(callTypeDescription)
    ? getCallStatusConfig(callStatusDescription).analyticsPageName
    : ":repairs:track";
};

const ServicePowerMessage = () => (
  <div className={styles.servicePowerMessage}>
    <p>
      If your Repair Reference starts with a 3, use our partner's{" "}
      <Link target="_blank" href="https://eu.portal.care/JLP/track">
        tracking site
      </Link>
      .
    </p>
    <p>If it starts with a 2, continue below.</p>
  </div>
);

export const getServerSideProps = async ctx => {
  const showMIAWChat = await isFeatureEnabled(ctx, "show_miaw_chat");

  return {
    props: {
      showMIAWChat,
    },
  };
};
function RepairTrackerForm({ showMIAWChat }) {
  const router = useRouter();
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (router.query.dt === "track") {
      analyticsTrigger("dataStateChange", getAnalyticsPageName(data), {}, "pageload");
    } else {
      analyticsTrigger("dataStateChange", ":repairs", {}, "pageload");
    }
  }, [router.query.dt]);

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        if (as.indexOf("dt") > -1 && data) {
          setShow(true);
        } else {
          setShow(false);
        }
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router, data]);
  const handleSubmit = async ({ email, callNumber }, onComplete) => {
    try {
      const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/self-serve/repair-tracker/v1/repairs`;
      const helpUIAxiosClient = await createAxiosClient({
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.NEXT_PUBLIC_REPAIR_TRACKER_API_KEY,
        },
      });

      const result = await helpUIAxiosClient.post(url, {
        email,
        callNumber,
      });
      console.log(`INFO: repair status fetched with code ${result.status}`);
      if (result.status === 200) {
        setData(result.data);
      } else if (result.status >= 400 && result.status < 500) {
        console.log(`INFO: repair status not found ${await result.text()}`);
        setError("Not found");
      } else {
        console.log(`ERROR: repair status failed to fetch ${await result.text()}`);
        setError("Internal server error");
      }
    } catch (err) {
      console.log(`ERROR: repair status fetch error ${err}`);
      setError("Internal server error");
    } finally {
      router.push("?dt=track");
      onComplete();
      setShow(true);
    }
  };

  return (
    <FeatureFlagProvider features={{ show_miaw_chat: showMIAWChat }}>
      {(!data && !error) || !show ? (
        <div className={styles.container}>
          <h1>Track your electrical repair</h1>
          <div className={classnames(styles.formContainer, styles.messageContainer)}>
            <Message
              type="information"
              title="Check your reference to track your repair"
              body={<ServicePowerMessage />}
            />
          </div>
          <div>
            <div className={styles.formContainer}>
              <Form
                formId="repairTrackerForm"
                fields={[EMAIL_ADDRESS, REPAIR_REFERENCE]}
                onSubmit={handleSubmit}
                submitButton="Track repair"
                hideFirstErrorLink
              />
              <div className={styles.infoBox}>
                <h5>What you can track online</h5>
                <p>
                  Our repair tracker online tool is for all repairs booked through our repair partner ServicePower. This
                  includes Televisions and all Small Electricals such as laptops, small household appliances and
                  wearable tech such as smartwatches. This does not include manufacturer arranged repairs for Large
                  Electrical items such as Washing machines, Fridges, Cookers etc.
                </p>
                <p>
                  You will know your repair has been booked through ServicePower if you receive an email from
                  NOREPLY@servicepower.com with a John Lewis and Partners Repair Receipt Ref.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <RepairTracker data={data} error={error} />
      )}
    </FeatureFlagProvider>
  );
}

RepairTrackerForm.metadata = metadata;

export default RepairTrackerForm;
